import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { 
  Settings, 
  Type, 
  Palette, 
  RotateCcw,
  Check
} from "lucide-react";
import { useUIConfig, fontFamilies, fontSizes, colorPresets } from "@/context/UIConfigContext";
import { toast } from "sonner";

const UISettingsDialog = ({ open, onOpenChange }) => {
  const { config, updateConfig, resetConfig } = useUIConfig();
  
  const handleReset = () => {
    resetConfig();
    toast.success("Configurações restauradas!");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configurações de UI
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Tipo de Letra */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Type className="w-4 h-4" />
              Tipo de Letra
            </Label>
            <Select 
              value={config.fontFamily} 
              onValueChange={(v) => updateConfig({ fontFamily: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {fontFamilies.map(font => (
                  <SelectItem key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                    {font.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Tamanho de Letra */}
          <div className="space-y-2">
            <Label>Tamanho de Letra</Label>
            <div className="grid grid-cols-3 gap-2">
              {fontSizes.map(size => (
                <Button
                  key={size.value}
                  variant={config.fontSize === size.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => updateConfig({ fontSize: size.value })}
                  className="justify-center"
                >
                  {size.label}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Cores */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Tema de Cores
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {colorPresets.map(preset => (
                <button
                  key={preset.label}
                  onClick={() => updateConfig({ 
                    primaryColor: preset.primary, 
                    accentColor: preset.accent 
                  })}
                  className={`flex items-center gap-2 p-2 rounded-lg border-2 transition-all ${
                    config.primaryColor === preset.primary 
                      ? 'border-violet-500 bg-violet-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex gap-1">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: preset.primary }}
                    />
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: preset.accent }}
                    />
                  </div>
                  <span className="text-xs">{preset.label}</span>
                  {config.primaryColor === preset.primary && (
                    <Check className="w-3 h-3 text-violet-500 ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </div>
          
          {/* Cor Primária Custom */}
          <div className="space-y-2">
            <Label>Cor Primária</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={config.primaryColor}
                onChange={(e) => updateConfig({ primaryColor: e.target.value })}
                className="w-10 h-10 rounded cursor-pointer border"
              />
              <input
                type="text"
                value={config.primaryColor}
                onChange={(e) => updateConfig({ primaryColor: e.target.value })}
                className="flex-1 px-3 py-2 border rounded text-sm font-mono"
              />
            </div>
          </div>
          
          {/* Cor de Destaque Custom */}
          <div className="space-y-2">
            <Label>Cor de Destaque</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={config.accentColor}
                onChange={(e) => updateConfig({ accentColor: e.target.value })}
                className="w-10 h-10 rounded cursor-pointer border"
              />
              <input
                type="text"
                value={config.accentColor}
                onChange={(e) => updateConfig({ accentColor: e.target.value })}
                className="flex-1 px-3 py-2 border rounded text-sm font-mono"
              />
            </div>
          </div>
          
          {/* Modo Compacto */}
          <div className="flex items-center justify-between">
            <div>
              <Label>Modo Compacto</Label>
              <p className="text-xs text-muted-foreground">Reduz espaçamentos</p>
            </div>
            <Switch 
              checked={config.compactMode}
              onCheckedChange={(v) => updateConfig({ compactMode: v })}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Restaurar
          </Button>
          <Button onClick={() => onOpenChange(false)}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UISettingsDialog;
